/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import CKEditor from 'ckeditor4-react'
import {
  Button, Checkbox, Collapse,
  FormControl, FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel, Radio, RadioGroup, Switch,
  TextField
} from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { useFormik } from 'formik'
import { planFormValidation } from '../../../../../utils/validationSchema'
import { FormLoader } from '../../../../Loader/FormLoader'
import { ButtonLoader } from '../../../../Loader/ButtonLoader'
import { SearchLoader } from '../../../../Loader/SearchLoader'
import { PlanConsumer } from '../../../../../PlanContext'
import { NamedRoutes } from '../../../../../routes'
import { useStyles } from './Styles'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { LanguageConsumer } from '../../../../../LanguageContext'
import { AuthenticatedUserConsumer } from '../../../../../AuthenticatedUserContext'
import AllowedTaxField from './AllowedTaxField'

const PlanForm = ({ currentDate, onSubmit, servicesList }) => {
  const classes = useStyles()
  const intl = useIntl()
  const history = useHistory()
  const [isLoading, setIsLoading] = React.useState(true)
  const PlanForm = useFormik({
    initialValues: {
      title: currentDate.title || '',
      description: currentDate.description || '',
      active: !!Object.keys(currentDate).length ? currentDate.active : true,
      services: currentDate.servicesIds || [],
      price_monthly: !!currentDate?.prices?.length ? (parseFloat((currentDate.prices.filter((price) => price.price_duration === 'monthly'))?.shift()?.price) >= 0 ? parseFloat((currentDate.prices.filter((price) => price.price_duration === 'monthly'))?.shift()?.price) : '') : '',
      price_quarterly: !!currentDate?.prices?.length ? (parseFloat((currentDate.prices.filter((price) => price.price_duration === 'quarterly'))?.shift()?.price) >= 0 ? parseFloat((currentDate.prices.filter((price) => price.price_duration === 'quarterly'))?.shift()?.price) : '') : '',
      price_midterm: !!currentDate?.prices?.length ? (parseFloat((currentDate.prices.filter((price) => price.price_duration === 'midterm'))?.shift()?.price) >= 0 ? parseFloat((currentDate.prices.filter((price) => price.price_duration === 'midterm'))?.shift()?.price) : '') : '',
      price_annually: !!currentDate?.prices?.length ? (parseFloat((currentDate.prices.filter((price) => price.price_duration === 'annually'))?.shift()?.price) >= 0 ? parseFloat((currentDate.prices.filter((price) => price.price_duration === 'annually'))?.shift()?.price) : '') : '',
      allowed_taxes: !!currentDate?.allowed_tax_types ? 'custom' : '',
      allowed_taxes_monthly: currentDate?.allowed_tax_types?.monthly || [],
      allowed_taxes_quarterly: currentDate?.allowed_tax_types?.quarterly || [],
      allowed_taxes_midterm: currentDate?.allowed_tax_types?.midterm || [],
      allowed_taxes_annually: currentDate?.allowed_tax_types?.annually || []
    },
    validateOnMount: true,
    validateOnBlur: false,
    validateOnChange: true,
    validationSchema: planFormValidation,
    onSubmit: ((values, actions) => {
      onSubmit(values, actions)
    })
  })

  const [activeValue, setActiveValue] = React.useState(PlanForm.values.active)
  const handleActiveChange = (event) => {
    setActiveValue(!JSON.parse(event.target.value))
    PlanForm.setFieldValue('active', !JSON.parse(event.target.value))
  }

  const [servicesValue, setServicesValue] = React.useState(servicesList.filter((service) => PlanForm.values.services.includes(service.id)))

  React.useEffect(() => {
      if (!!servicesList && !!servicesList.length) {
        setServicesValue(servicesList.filter((service) => PlanForm.values.services.includes(service.id)))
      }
    },// eslint-disable-next-line react-hooks/exhaustive-deps
    [servicesList])

  const [allowedTaxTypes, setAllowedTaxTypes] = React.useState(PlanForm.values.allowed_taxes)
  const handleChange = (event) => {
    setAllowedTaxTypes(event.target.value)
    PlanForm.setFieldValue('allowed_taxes', event.target.value)
  }

  let errorClass = PlanForm.errors.description ? ' Mui-error' : ''
  return (
    <PlanConsumer>
      {({ servicesListLoading }) => (
        <form onSubmit={PlanForm.handleSubmit} noValidate autoComplete="off"
              className={classes.formWrapper}>

          <div className={classes.BasicInfoRow}>
            <InputLabel id="Plan.Title"> {<FormattedMessage id='Fields.label.PlanTitle'/>} </InputLabel>
            <FormControl variant="outlined">
              <TextField
                placeholder={intl.formatMessage({ id: 'Plan.placeholder.PlanTitle' })}
                id="Plan.Title"
                type={'text'}
                margin="dense"
                variant="outlined"
                name='title'
                value={PlanForm.values.title}
                onChange={PlanForm.handleChange}
                onBlur={PlanForm.handleBlur}
                helperText={PlanForm.errors.title && PlanForm.touched.title && PlanForm.errors.title}
                error={PlanForm.errors.title && PlanForm.touched.title !== undefined}
              />
            </FormControl>
          </div>

          <div className={classes.BasicInfoRow}>
            <InputLabel id="Plan.Description"><FormattedMessage id='Fields.label.PlanDescription'/></InputLabel>
            <FormControl variant="outlined">
              <LanguageConsumer>
                {({ locale }) => (
                  <div className={classes.PageCKContent + errorClass}>
                    {!!isLoading && <SearchLoader loading={true}/>}
                    <CKEditor
                      placeholder={intl.formatMessage({ id: 'Fields.label.PlanDescription' })}
                      required
                      fullWidth
                      name='description'
                      variant="outlined"
                      data={PlanForm.values.description}
                      config={{
                        removeButtons: 'About',
                        language: locale
                      }}
                      onChange={(event, editor) => {
                        PlanForm.setFieldValue('description', event.editor.getData() ? event.editor.getData() : '' || '')
                      }}
                      onloaded={(event) => {
                        setIsLoading(false)
                      }}
                      onOpen={PlanForm.handleBlur}
                      helperText={PlanForm.errors.description && PlanForm.touched.description && PlanForm.errors.description}
                      error={PlanForm.errors.description && PlanForm.touched.description !== undefined}
                    />
                    {PlanForm.errors.description &&
                    <FormHelperText error required>{PlanForm.errors.description}</FormHelperText>
                    }
                  </div>
                )}
              </LanguageConsumer>
            </FormControl>
          </div>

          <div className={classes.BasicInfoRow}>
            <InputLabel id="Plan.Services"><FormattedMessage id='Fields.label.Services'/></InputLabel>
            <Autocomplete
              multiple
              id="Plan.Services"
              loading={!!servicesListLoading}
              loadingText={<ButtonLoader loading={true}/>}
              options={servicesList || []}
              value={servicesValue}
              freeSolo
              disableCloseOnSelect
              getOptionLabel={(option) => option.title}
              onChange={(event, value) => {
                setServicesValue(value)
                let servicesIds = (!!value && !!value.length) ?
                  value.map(service => service.id) : []
                PlanForm.setFieldTouched('services', true, true)
                PlanForm.setFieldValue('services', servicesIds)
              }}
              renderOption={(option, { selected }) => (
                <React.Fragment>
                  <Checkbox
                    color={'primary'}
                    checked={PlanForm.values.services.includes(option.id)}
                  />
                  {option.title}
                </React.Fragment>
              )}
              renderInput={(params) => (
                <TextField {...params} variant="outlined"
                           placeholder={intl.formatMessage({ id: 'Plans.List.Table.Services' })}
                           name='services'
                           onChange={PlanForm.handleChange}
                           onBlur={PlanForm.handleBlur}
                           helperText={PlanForm.errors.services && PlanForm.touched.services && PlanForm.errors.services}
                           error={PlanForm.errors.services && PlanForm.touched.services !== undefined}
                />
              )}
            />
          </div>

          <div className={classes.BasicInfoRow}>
            <InputLabel id="Plan.AllowedTax"><FormattedMessage
              id='Fields.label.AllowedTaxTypes'/>:</InputLabel>
            <FormControl variant="outlined">
              <RadioGroup className={classes.radiosWrapper} aria-label="AllowedTaxTypes" name="allowed_taxes"
                          value={allowedTaxTypes}
                          onChange={handleChange}>
                <FormControlLabel value="" control={<Radio color={'primary'}/>}
                                  label={<FormattedMessage id='Fields.label.AllowedTaxTypes.all'/>}/>
                <FormControlLabel value="custom" control={<Radio color={'primary'}/>}
                                  label={<FormattedMessage id='Fields.label.AllowedTaxTypes.custom'/>}/>
              </RadioGroup>
              <Collapse in={!!allowedTaxTypes} timeout="auto" unmountOnExit>
                <AuthenticatedUserConsumer>
                  {({ bootData }) => (
                    <div className={classes.TaxesWrapper}>
                      <AllowedTaxField fieldName={'allowed_taxes_monthly'} labelKey={'Taxes.Monthly.label'}
                                       allowedTaxTypes={bootData?.allowed_tax_types || []}
                                       formAction={PlanForm} placeholderKey={'Taxes.Monthly'}/>
                      <AllowedTaxField fieldName={'allowed_taxes_quarterly'} labelKey={'Taxes.Quarterly.label'}
                                       allowedTaxTypes={bootData?.allowed_tax_types || []}
                                       formAction={PlanForm} placeholderKey={'Taxes.Quarterly'}/>
                      <AllowedTaxField fieldName={'allowed_taxes_midterm'} labelKey={'Taxes.Midterm.label'}
                                       allowedTaxTypes={bootData?.allowed_tax_types || []}
                                       formAction={PlanForm} placeholderKey={'Taxes.Midterm'}/>
                      <AllowedTaxField fieldName={'allowed_taxes_annually'} labelKey={'Taxes.Annually.label'}
                                       allowedTaxTypes={bootData?.allowed_tax_types || []}
                                       formAction={PlanForm} placeholderKey={'Taxes.Annually'}/>
                    </div>
                  )}
                </AuthenticatedUserConsumer>
              </Collapse>
            </FormControl>
          </div>

          <div className={classes.BasicInfoRow}>
            <InputLabel id="Service.Prices"><FormattedMessage id='Plans.List.Table.Prices'/>*:</InputLabel>
            <FormControl variant="outlined">
              <div className={classes.pricesWrapper}>
                <TextField
                  label={intl.formatMessage({ id: 'Service.Prices.Monthly' })}
                  id="price_monthly"
                  type={'number'}
                  margin="dense"
                  variant="outlined"
                  name='price_monthly'
                  value={PlanForm.values.price_monthly}
                  onChange={PlanForm.handleChange}
                  onBlur={PlanForm.handleBlur}
                  helperText={PlanForm.errors.price_monthly && PlanForm.touched.price_monthly && PlanForm.errors.price_monthly}
                  error={PlanForm.errors.price_monthly && PlanForm.touched.price_monthly !== undefined}
                  InputLabelProps={{
                    shrink: true
                  }}
                  InputProps={{
                    inputProps: {
                      min: 1
                    },
                    endAdornment: <InputAdornment position="end"><FormattedMessage id='egp'/></InputAdornment>
                  }}
                />
                <TextField
                  label={intl.formatMessage({ id: 'Service.Prices.Quarterly' })}
                  id="price_quarterly"
                  type={'number'}
                  margin="dense"
                  variant="outlined"
                  name='price_quarterly'
                  value={PlanForm.values.price_quarterly}
                  onChange={PlanForm.handleChange}
                  onBlur={PlanForm.handleBlur}
                  helperText={PlanForm.errors.price_quarterly && PlanForm.touched.price_quarterly && PlanForm.errors.price_quarterly}
                  error={PlanForm.errors.price_quarterly && PlanForm.touched.price_quarterly !== undefined}
                  InputLabelProps={{
                    shrink: true
                  }}
                  InputProps={{
                    inputProps: {
                      min: 1
                    },
                    endAdornment: <InputAdornment position="end"><FormattedMessage id='egp'/></InputAdornment>
                  }}
                />
                <TextField
                  label={intl.formatMessage({ id: 'Service.Prices.Midterm' })}
                  id="price_midterm"
                  type={'number'}
                  margin="dense"
                  variant="outlined"
                  name='price_midterm'
                  value={PlanForm.values.price_midterm}
                  onChange={PlanForm.handleChange}
                  onBlur={PlanForm.handleBlur}
                  helperText={PlanForm.errors.price_midterm && PlanForm.touched.price_midterm && PlanForm.errors.price_midterm}
                  error={PlanForm.errors.price_midterm && PlanForm.touched.price_midterm !== undefined}
                  InputLabelProps={{
                    shrink: true
                  }}
                  InputProps={{
                    inputProps: {
                      min: 1
                    },
                    endAdornment: <InputAdornment position="end"><FormattedMessage id='egp'/></InputAdornment>
                  }}
                />
                <TextField
                  label={intl.formatMessage({ id: 'Service.Prices.Annually' })}
                  id="price_annually"
                  type={'number'}
                  margin="dense"
                  variant="outlined"
                  name='price_annually'
                  value={PlanForm.values.price_annually}
                  onChange={PlanForm.handleChange}
                  onBlur={PlanForm.handleBlur}
                  helperText={PlanForm.errors.price_annually && PlanForm.touched.price_annually && PlanForm.errors.price_annually}
                  error={PlanForm.errors.price_annually && PlanForm.touched.price_annually !== undefined}
                  InputLabelProps={{
                    shrink: true
                  }}
                  InputProps={{
                    inputProps: {
                      min: 1
                    },
                    endAdornment: <InputAdornment position="end"><FormattedMessage id='egp'/></InputAdornment>
                  }}
                />
              </div>
              <FormHelperText error>{PlanForm.dirty &&
              (PlanForm.touched.price_monthly ||
                PlanForm.touched.price_quarterly ||
                PlanForm.touched.price_midterm ||
                PlanForm.touched.price_annually) &&
              PlanForm.errors.pricesPlan}</FormHelperText>
            </FormControl>
          </div>

          <div className={classes.BasicInfoRow}>
            <InputLabel id="Service.Active"><FormattedMessage
              id='Plans.List.Table.active'/>:</InputLabel>
            <FormControl variant="outlined" className={classes.SwitchWrapper}>
              <FormControlLabel
                label={activeValue ? intl.formatMessage({ id: 'Services.List.Table.Active.Option.Active' }) : intl.formatMessage({ id: 'Services.List.Table.Active.Option.inActive' })}
                value={activeValue ? true : false}
                control={
                  <Switch color="primary" checked={activeValue} value={activeValue ? true : false}/>
                }
                onChange={handleActiveChange}
              />
            </FormControl>
          </div>

          <div className={classes.SubmitRow}>
            <button type="submit" disabled={PlanForm.isSubmitting || !PlanForm.isValid}
                    className={classes.AddAdminSubmit}>
              <FormattedMessage id='AdditionalInfo.button.submit'/>
            </button>
            <Button onClick={() => history.push(NamedRoutes.subscription.plan.list)} color="primary">
              <FormattedMessage id='ChangePhone.DialogActions.cancel'/>
            </Button>
          </div>
          {PlanForm.isSubmitting && <FormLoader loading={true}/>}
        </form>
      )}
    </PlanConsumer>
  )
}

export default PlanForm
